import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };

export type TransferLineSerialRow = {
  transfer_line_serial_id: string;
  asset_id?: string | null;
  item_id?: string | null;
  status?: string | null;
  shipped_qty?: number | null;
  serial_number?: string | null;
};

export type TransferLineRow = {
  transfer_line_id: string;
  transfer_id: string;
  item_id?: string | null;
  product_id?: string | null;
  asset_id?: string | null;
  vehicle_id?: string | null;
  external_item_name?: string | null;
  external_item_code?: string | null;
  external_item_type?: 'external' | 'accessory' | string | null;
  qty: number | null;
  status?: 'pending' | 'picked' | 'shipped' | 'in_transit' | 'received' | 'kept' | 'cancelled' | null;
  shipped_qty?: number | null;
  received_qty?: number | null;
  kept_qty?: number | null;
  is_permanent?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  expected_return_at?: string | null;
  picked_at?: string | null;
  shipped_at?: string | null;
  received_at?: string | null;
  picked_by?: string | null;
  shipped_by?: string | null;
  received_by?: string | null;
  product?: { product_code?: string | null; name_public?: string | null } | null;
  asset?: { asset_id?: string; serial_number?: string | null; barcode?: string | null } | null;
  vehicle?: { vehicle_id?: string; name?: string | null; vehicle_type?: string | null; license_plate?: string | null } | null;
  item?: { item_code?: string | null; name_internal?: string | null } | null;
  transfer_line_serials?: TransferLineSerialRow[] | null;
};

export type LocationInfo = {
  location_id?: string;
  name?: string | null;
  display_name?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
};

export type LogisticsModeInfo = {
  mode_id?: string;
  name?: string | null;
  mode_type?: string | null;
  carrier?: string | null;
};

export type TransferOrderRow = {
  transfer_id: string;
  doc_no?: string | null;
  description?: string | null;
  status?: 'draft' | 'confirmed' | 'partially_picked' | 'picked' | 'partially_shipped' | 'shipped' | 'in_transit' | 'partially_received' | 'received' | 'complete' | 'cancelled' | null;
  created_at?: string | null;
  updated_at?: string | null;
  from_location_id?: string | null;
  to_location_id?: string | null;
  service_ticket_id?: string | null;
  need_by_at?: string | null;
  expected_ship_at?: string | null;
  expected_receive_at?: string | null;
  actual_ship_at?: string | null;
  actual_receive_at?: string | null;
  transfer_scope?: 'temporary' | 'permanent' | string | null;
  expected_return_at?: string | null;
  parent_transfer_id?: string | null;
  return_transfer_id?: string | null;
  need_by?: string | null; // Legacy alias
  eta?: string | null; // Legacy alias
  tracking_no?: string | null;
  logistics_mode_id?: string | null;
  r2_mode_of_transfer?: number | null;
  from?: LocationInfo | null;
  to?: LocationInfo | null;
  logistics_mode?: LogisticsModeInfo | null;
  transfer_lines?: TransferLineRow[] | null;
  transfer_waypoints?: TransferWaypointRow[] | null;
  [key: string]: any;
};

export type TransferWaypointRow = {
  waypoint_id: string;
  transfer_id: string;
  location_id: string;
  sequence: number;
  status?: 'pending' | 'arrived' | 'available' | 'picked' | 'shipped' | 'skipped' | null;
  expected_arrive_at?: string | null;
  expected_depart_at?: string | null;
  actual_arrive_at?: string | null;
  actual_depart_at?: string | null;
  notes?: string | null;
  received_by?: string | null;
  shipped_by?: string | null;
  location?: { name?: string | null } | null;
};

export type PermanentTransferRequestRow = {
  request_id: string;
  transfer_line_id: string;
  from_location_id: string;
  to_location_id: string;
  item_id: string;
  asset_id?: string | null;
  requested_qty: number;
  reason?: string | null;
  status?: 'pending' | 'approved' | 'rejected' | 'expired' | null;
  requested_by: string;
  requested_at: string;
  decided_by?: string | null;
  decided_at?: string | null;
  decision_note?: string | null;
  expires_at?: string | null;
  item?: { name?: string | null } | null;
  asset?: { serial_number?: string | null } | null;
  requested_by_profile?: { full_name?: string | null } | null;
  decided_by_profile?: { full_name?: string | null } | null;
};

// Fetch permanent transfer requests for a transfer order
export async function fetchPermanentTransferRequests(params: {
  transferId: string;
  supabase?: SupabaseClient;
}): Promise<{ data: PermanentTransferRequestRow[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  if (!params.transferId) return { data: [], error: null };
  
  // Get line IDs for this transfer
  const { data: lines, error: linesError } = await supabase
    .from('transfer_lines')
    .select('transfer_line_id')
    .eq('transfer_id', params.transferId);
  
  if (linesError || !lines || lines.length === 0) {
    return { data: [], error: linesError };
  }
  
  const lineIds = lines.map(l => l.transfer_line_id);
  
  const { data, error } = await supabase
    .from('permanent_transfer_requests')
    .select(
      `*,
       item:items(item_id,name:name_internal),
       asset:assets(asset_id,serial_number),
       decided_by_profile:user_profiles!permanent_transfer_requests_decided_by_fkey(id,full_name)`
    )
    .in('transfer_line_id', lineIds)
    .order('requested_at', { ascending: false });

  if (error) return { data: [], error };
  const rows = Array.isArray(data) ? (data as PermanentTransferRequestRow[]) : [];

  // requested_by is a login id (its key references auth.users, which PostgREST cannot embed), so the
  // requester's name comes from the profile linked to that login.
  const requesterIds = [...new Set(rows.map((row) => row.requested_by).filter(Boolean))];
  if (requesterIds.length === 0) return { data: rows, error: null };
  const { data: requesters, error: requestersError } = await supabase
    .from('user_profiles')
    .select('auth_user_id,full_name')
    .in('auth_user_id', requesterIds);
  if (requestersError) return { data: [], error: requestersError };
  const requesterByLogin = new Map((requesters || []).map((profile) => [profile.auth_user_id, { full_name: profile.full_name }]));
  return {
    data: rows.map((row) => ({ ...row, requested_by_profile: requesterByLogin.get(row.requested_by) ?? null })),
    error: null,
  };
}

export const permanentTransferRequestsQueryKey = (transferId: string) => [
  'permanent-transfer-requests',
  transferId,
];

export const getPermanentTransferRequestsQueryOptions = ({
  transferId,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  transferId: string;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: permanentTransferRequestsQueryKey(transferId),
  enabled: enabled && Boolean(transferId),
  queryFn: async () => {
    const { data, error } = await fetchPermanentTransferRequests({ transferId, supabase });
    if (error) throw error;
    return data;
  },
});

/** Fetch transfer broadcast locations for a given shortage. */
export async function fetchBroadcastLocationsForShortage(
  shortageId: string,
  options?: ClientOption,
): Promise<{ data: string[]; error: PostgrestError | null }> {
  const client = options?.supabase ?? getSupabaseClient();
  const { data, error } = await client
    .from('transfer_broadcasts')
    .select('to_location_id')
    .eq('shortage_id', shortageId);
  if (error) return { data: [], error };
  const locationIds = (data ?? []).map((b: any) => b.to_location_id).filter(Boolean);
  return { data: locationIds, error: null };
}

function isMissingBatchAvailabilityV2(error: PostgrestError | null | undefined): boolean {
  if (!error) return false;
  if (error.code === '42883' || error.code === 'PGRST202') return true;
  return /does not exist|could not find the function/i.test(error.message ?? '');
}

/** Fetch transfer-request availability through the shared v2 contract (per location). */
export async function fetchTransferAvailabilityBatch(
  candidates: any[],
  locationId: string,
  options?: ClientOption,
): Promise<{ data: any[]; error: PostgrestError | null }> {
  const client = options?.supabase ?? getSupabaseClient();
  const rpcParams = {
    p_candidates: candidates,
    p_location_id: locationId,
  };
  const { data, error } = await (client.rpc as any)('batch_availability_snapshot_v2', rpcParams);
  if (isMissingBatchAvailabilityV2(error)) {
    const fallback = await (client.rpc as any)('batch_quote_item_overlap_snapshot', rpcParams);
    return {
      data: Array.isArray(fallback.data) ? fallback.data : [],
      error: fallback.error ?? null,
    };
  }
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}
